/**
 * Issuer Hub Route Wrapper
 *
 * Wraps issuer-hub routes with the IssuerHubProvider context so that child
 * components can access issuer identity and permissions via useIssuerHubContext().
 *
 * Render states:
 *  1. Loading — shows a centred spinner while permissions resolve.
 *  2. Ready   — computes issuerRole from access signals and renders
 *     children inside an <IssuerHubProvider>.
 */

import { Navigate } from "react-router-dom";
import { useIssuerHubAccess } from "@/hooks/useIssuerHubAccess";
import { useAuth } from "@/hooks/useAuth";
import { IssuerHubProvider } from "@/components/issuer-hub/IssuerHubContext";
import type { IssuerHubContextType } from "@/types/IssuerPipeline";
import { FlexLayout, Spinner } from "@/design-system/salt";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface IssuerHubGuardProps {
  children: React.ReactNode;
  /** When "editor", redirect viewer-only users to /action-center. */
  requiredAccess?: "viewer" | "editor";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Issuer Hub Guard component. */
export default function IssuerHubGuard({
  children,
  requiredAccess,
}: IssuerHubGuardProps) {
  const { hasAccess, canWrite, issuerCorporateId, accessLevel, loading } = useIssuerHubAccess();
  const { hasRole } = useAuth();

  // ── Loading ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <FlexLayout align="center" justify="center" style={{minHeight: "100vh", background: "var(--salt-container-primary-background)"}}>
        <Spinner aria-label="Loading issuer workspace" size="large" />
      </FlexLayout>
    );
  }

  // ── Access denied ────────────────────────────────────────────────────────
  if (!hasAccess || !issuerCorporateId) {
    return <Navigate to="/action-center" replace />;
  }

  if (requiredAccess === "editor" && !canWrite) {
    return <Navigate to="/action-center" replace />;
  }

  // ── Determine issuer role ────────────────────────────────────────────────
  const issuerRole: IssuerHubContextType["issuerRole"] = hasRole("admin")
    ? "platform_admin"
    : accessLevel === "owner" || accessLevel === "editor"
      ? "issuer_manager"
      : "issuer_rep";

  const contextValue: IssuerHubContextType = {
    issuerCorporateId,
    canWrite,
    issuerRole,
  };

  return <IssuerHubProvider value={contextValue}>{children}</IssuerHubProvider>;
}
